<script setup lang="ts">
import { useCrud } from "~/composables/use-crud";

defineOptions({ name: "LeadFormModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { $api } = useNuxtApp();
const { t, te } = useI18n();
const toast = useToast();

const isOpen = ref(false);
const admins = ref<{ id: string; fullName: string }[]>([]);

interface Lead {
  id: string;
  taxCode: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  website: string;
  facebook: string;
  address: string;
  province: string;
  industry: string;
  note: string;
  status: string;
  tags: string[];
  assignedID: string;
  consentTime: number;
  source: string;
  sourceKey: string;
  sourceURL: string;
}

const { formModel, formErrors, errorMsg, isCreate, saveLoading, save, load, reset } =
  useCrud<Lead>({
    endpoint: "/leads",
    defaultFormModel: () => ({
      id: "",
      taxCode: "",
      companyName: "",
      contactName: "",
      phone: "",
      email: "",
      website: "",
      facebook: "",
      address: "",
      province: "",
      industry: "",
      note: "",
      status: "new",
      tags: [],
      assignedID: "",
      consentTime: 0,
      source: "manual",
      sourceKey: "",
      sourceURL: "",
    }),
  });

// An unassigned lead comes back as a zero ObjectID, which matches none of the
// options and leaves the select blank rather than showing "Chưa giao".
const assignedID = computed({
  get: () => (/^0+$/.test(formModel.assignedID || "") ? "" : formModel.assignedID),
  set: (value: string) => {
    formModel.assignedID = value;
  },
});

// Stored as 84xxxxxxxxx so dedupe and Zalo agree on one shape, but nobody in
// Vietnam reads a number that way. Shown and edited as 0xxxxxxxxx; admin-api
// normalises whatever is typed back on save.
const phone = computed({
  get: () => {
    const value = formModel.phone || "";
    return value.startsWith("84") ? `0${value.slice(2)}` : value;
  },
  set: (value: string) => {
    formModel.phone = value;
  },
});

const statusOptions = computed(() =>
  ["new", "contacted", "replied", "qualified", "won", "lost"].map((value) => ({
    value,
    label: t(`lead.status_${value}`),
  })),
);

const assigneeOptions = computed(() => [
  { value: "", label: t("lead.unassigned") },
  ...admins.value.map((admin) => ({ value: admin.id, label: admin.fullName })),
]);

async function open(id?: string) {
  reset();
  if (id) await load(id);

  // Loaded per open rather than on mount: the modal is rarely opened, and an
  // admin added since the page loaded should still be pickable.
  try {
    const res = await $api<{ items: { id: string; fullName: string }[] }>("/admin-users", {
      params: { pageSize: 50 },
    });
    admins.value = res.items || [];
  } catch {
    admins.value = [];
  }

  isOpen.value = true;
}

async function submit() {
  try {
    await save();
    toast.success({ title: t(isCreate.value ? "message.createSuccess" : "message.updateSuccess") });
    isOpen.value = false;
    emit("saved");
  } catch {
    // errors surface via errorMsg / formErrors
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="t(isCreate ? 'lead.newTitle' : 'lead.updateTitle')"
    size="xl"
  >
    <div class="flex flex-col gap-medium">
      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>

      <!-- Two columns from md up: who the business is on the left, how we are
           working it on the right. Fields stack within a column instead of
           pairing off again — half an xl modal split a second time leaves an
           input too narrow to read a Vietnamese company name in. -->
      <div class="flex flex-col gap-medium md:flex-row md:gap-large">
        <div class="flex w-full flex-col gap-medium md:w-1/2">
          <FormField
            v-model="formModel.companyName"
            :label="t('lead.company')"
            :error="formErrors.companyName"
          />
          <FormField
            v-model="formModel.taxCode"
            :label="t('lead.taxCode')"
            :error="formErrors.taxCode"
          />
          <FormField
            v-model="formModel.contactName"
            :label="t('lead.contact')"
            :error="formErrors.contactName"
          />
          <FormField
            v-model="phone"
            :label="t('field.phone')"
            :error="formErrors.phone"
          />
          <FormField
            v-model="formModel.email"
            type="email"
            :label="t('field.email')"
            :error="formErrors.email"
          />
          <FormField
            v-model="formModel.website"
            :label="t('lead.website')"
            :error="formErrors.website"
          />
          <FormField
            v-model="formModel.facebook"
            :label="t('lead.facebook')"
            :error="formErrors.facebook"
          />
        </div>

        <div class="flex w-full flex-col gap-medium md:w-1/2">
          <FormField
            v-model="formModel.address"
            :label="t('lead.address')"
            :error="formErrors.address"
          />
          <FormField
            v-model="formModel.province"
            :label="t('lead.province')"
            :error="formErrors.province"
          />
          <FormField
            v-model="formModel.industry"
            :label="t('lead.industry')"
            :error="formErrors.industry"
          />
          <FormSelect
            v-model="formModel.status"
            :label="t('field.status')"
            :options="statusOptions"
            :error="formErrors.status"
          />
          <FormSelect
            v-model="assignedID"
            :label="t('lead.owner')"
            :options="assigneeOptions"
            :error="formErrors.assignedID"
          />
          <!-- Free-form, and "zalo-sent" is one of these like any other: the
               sender writes it, and removing it here is how an operator puts a
               lead the sender marked by mistake back in the queue. -->
          <FormTagsInput
            v-model="formModel.tags"
            :label="t('lead.tags')"
            :placeholder="t('lead.tagsPlaceholder')"
            :help-text="t('lead.tagsHelp')"
            :error="formErrors.tags"
          />
          <FormDatePicker
            v-model="formModel.consentTime"
            :label="t('lead.consentTime')"
            :help-text="t('lead.consentTimeHelp')"
            :error="formErrors.consentTime"
          />
          <FormTextarea
            v-model="formModel.note"
            :label="t('lead.note')"
            :error="formErrors.note"
            :rows="3"
          />
        </div>
      </div>
    </div>

    <template #actions>
      <Button variant="tertiary" size="small" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button variant="primary" size="small" :disabled="saveLoading" @click="submit">
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
